import { Canvas } from "@react-three/fiber";
import {
  Environment,
  PerspectiveCamera,
  OrbitControls,
} from "@react-three/drei";
import ThreeJsScene from "./threeJsScene";
import styled from "@emotion/styled";
import React, { Suspense, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Leva, useControls, button, useCreateStore, folder } from "leva";
import useThreeJsLoader from "../../../zustand/stores/useThreeJsLoader";
import { useShallow } from "zustand/react/shallow";
import LoadingScreen from "./LoadingScreen";
import { useViewport } from "../../../helpers";
import { useSnackbar } from "notistack";
import "./threejs-loader.css";

const PREFIX = "ThreeJsLoader";

const classes = {
  canvas: `${PREFIX}-canvas`,
  leva: `${PREFIX}-leva`,
};

const ThreeJsLoaderContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.canvas}`]: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      marginTop: "10px",
    },
  };
});

export default function ThreeJsLoader() {
  const { width } = useViewport();
  const modelUrl = useThreeJsLoader((state) => state.modelUrl);
  const setRegeneratePreview = useThreeJsLoader(
    (state) => state.setRegeneratePreview
  );
  const colorsStore = useCreateStore();
  const radiiStore = useCreateStore();
  const spaceStore = useCreateStore();
  const fontSizesStore = useCreateStore();
  const sizesStore = useCreateStore();
  const borderWidthsStore = useCreateStore();
  const fontWeightsStore = useCreateStore();

  // Handle Leva Colors
  const colors = useControls(
    {
      colors: folder({
        // Background Color Title Bar
        elevation1: width >= 768 ? "#d3af5f" : "#424242", // FAZ YELLOW
        // Background Color Content
        elevation2: "#363636", // FAZ DARK GREY
        // Background Color Inputs
        elevation3: "#4c4c4c", // FAZ LIGHT GREY
        // Border Color Inputs
        accent1: "#d3af5f",
        // Buttons Color
        accent2: "#d3af5f",
        // Hover Buttons Border Color
        accent3: "#937a42",
        // Icons & Under Curves Color
        highlight1: "#363636",
        // Text Color
        highlight2: "#cfcecc",
        // Folders & Buttons Text Color
        highlight3: "#ffffff",
        // ???
        // vivid1: "#ffcc00",
      }),
    },
    { store: colorsStore }
  );

  // Handle Leva Border Radius
  const radii = useControls(
    {
      radii: folder({
        // Sliders & Intervals Border Radius
        xs: "2px",
        // Buttons and Inpupts Border Radius
        sm: "4px",
        // Leva Panels Border Radius
        lg: "10px",
      }),
    },
    { store: radiiStore }
  );

  // Handle Spaces Between Controls
  const space = useControls(
    {
      space: folder({
        // Vertical Spacing
        sm: "3px",
        // sm: "6px",
        // Horizontal Spacing
        md: "10px",
        // Vertical Gap
        rowGap: "5px",
        // Horizontal Gap
        colGap: "7px",
      }),
    },
    { store: spaceStore }
  );

  // Handle Leva Font Sizes
  const fontSizes = useControls(
    {
      fontSizes: folder({
        root: "16px",
      }),
    },
    { store: fontSizesStore }
  );

  // Handle Leva Dimensions
  const sizes = useControls(
    {
      sizes: folder({
        // Leva Panel Width
        rootWidth: "250px",
        // Leva Inputs Width
        controlWidth: "160px",
        // Interval Scrubber Width
        scrubberWidth: "8px",
        // Interval Scrubber Height
        scrubberHeight: "16px",
        rowHeight: "24px",
        // folderHeight: "20px",
        checkboxSize: "16px",
        joystickWidth: "100px",
        joystickHeight: "100px",
        colorPickerWidth: "160px",
        colorPickerHeight: "160px",
        monitorHeight: "60px",
        // Title Bar Height
        titleBarHeight: "43px",
      }),
    },
    { store: sizesStore }
  );

  const borderWidths = useControls(
    {
      borderWidths: folder({
        root: "0px",
        input: "1px",
        focus: "1px",
        hover: "1px",
        active: "1px",
        folder: "1px",
      }),
    },
    { store: borderWidthsStore }
  );

  const fontWeights = useControls(
    {
      fontWeights: folder({
        // Controls Font Weight
        label: {
          value: "normal",
          // options: ["bold", "light"]
        },
        // Folders Font Weight
        folder: {
          value: "bold",
          // options: ["bold", "light"]
        },
        // Buttons Font Weight
        button: {
          value: "bold",
          // options: ["bold", "light"]
        },
      }),
    },
    { store: fontWeightsStore }
  );

  const theme = {
    colors,
    radii,
    space,
    fontSizes,
    sizes,
    borderWidths,
    fontWeights,
  };

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let levaPanel;
    let inter = setInterval(() => {
      levaPanel = document.querySelector(".leva-c-kWgxhW");
      if (levaPanel !== null) {
        levaPanel.style.position = "absolute";
        levaPanel.style.top = width >= 768 ? "0px" : "280px";
        width >= 768
          ? (levaPanel.style.right = "0px")
          : (levaPanel.style.left = "0px");
        levaPanel.style.width =
          width >= 768 ? (width <= 844 ? "40%" : "23%") : "100%";
        width >= 768
          ? (levaPanel.style.paddingBottom = "0px")
          : (levaPanel.style.paddingBottom = "15px");
        clearInterval(inter);
      }
    }, 100);
  }, []);

  const { cameraInitialPosition, isSaving, setCanvasReady } = useThreeJsLoader(
    useShallow((state) => {
      return {
        cameraInitialPosition: state.cameraInitialPosition,
        isSaving: state.isSaving,
        setCanvasReady: state.setCanvasReady,
      };
    })
  );

  const cameraRef = useRef();

  const {
    [t("leva.threeJsLoader.backgroundOptions")]: BackgroundOptions,
    [t("leva.threeJsLoader.blur")]: Blur,
  } = useControls({
    [t("leva.threeJsLoader.clipboard")]: button(() => {
      navigator.clipboard.writeText(modelUrl);
      enqueueSnackbar(t("toasts.copiedInClipboard"), {
        variant: "info",
      });
    }),
    [t("leva.threeJsLoader.regeneratePreview")]: button(() => {
      setRegeneratePreview(true);
    }),
    [t("leva.threeJsLoader.resetCameraPosition")]: button(() => {
      cameraRef.current.position.set(
        cameraInitialPosition[0],
        cameraInitialPosition[1],
        cameraInitialPosition[2]
      );
    }),
    [t("leva.threeJsLoader.skybox")]: folder(
      {
        [t("leva.threeJsLoader.backgroundOptions")]: {
          options: {
            [t("leva.threeJsLoader.basic")]: `/belfast_farmhouse_2k${
              width <= 768 ? "_mobile" : ""
            }.hdr`,
            [t("leva.threeJsLoader.harbour")]: `/golden_bay_2k${
              width <= 768 ? "_mobile" : ""
            }.hdr`,
            [t("leva.threeJsLoader.night")]: `/kloppenheim_02_2k${
              width <= 768 ? "_mobile" : ""
            }.hdr`,
            [t("leva.threeJsLoader.meadow")]: `/meadow_2_2k${
              width <= 768 ? "_mobile" : ""
            }.hdr`,
            [t(
              "leva.threeJsLoader.sky"
            )]: `/kloofendal_48d_partly_cloudy_puresky_2k${
              width <= 768 ? "_mobile" : ""
            }.hdr`,
            [t("leva.threeJsLoader.snow")]: `/snowy_park_01_2k${
              width <= 768 ? "_mobile" : ""
            }.hdr`,
          },
        },
        [t("leva.threeJsLoader.blur")]: {
          value: 0,
          min: 0,
          max: 0.5,
          step: 0.05,
        },
      },
      {
        order: 5,
        collapsed: true,
      }
    ),
  });

  return (
    <ThreeJsLoaderContainer>
      <div className={classes.canvas}>
        <Canvas
          flat
          style={{
            width: "100%",
            height: width >= 768 ? (width <= 844 ? "370px" : "680px") : "280px",
          }}
          dpr={[1, 1]}
          onCreated={setCanvasReady}
        >
          <PerspectiveCamera
            ref={cameraRef}
            position={cameraInitialPosition}
            makeDefault
            near={0.1}
            far={50}
          />
          <OrbitControls
            makeDefault={false}
            enablePan={false}
            minDistance={1.5}
            maxDistance={40}
            maxPolarAngle={Math.PI / 2 + 0.1}
            target={[0, 1.5, 0]}
          />
          <Environment background files={BackgroundOptions} blur={Blur} />
          <Suspense fallback={null}>
            <ThreeJsScene />
          </Suspense>
        </Canvas>
        <LoadingScreen />
        <Leva
          flat
          titleBar={width >= 768}
          hidden={isSaving}
          theme={theme}
          collapsed={width >= 768}
        />
      </div>
    </ThreeJsLoaderContainer>
  );
}
